// User administration (design system 06-modules §1). Answers the five questions on one screen:
// who is waiting (R1, oldest first, always present), who has access and how many (R2, total and
// matching counts), where each person's rights come from (chips per source, in the row), whether
// the account is dormant (last active), and what was decided when and by whom. Filters cover the
// whole loaded set (R3); every mutation lives in a dialog or on the account page (R4), never as an
// inline control that writes on change. Superuser-only operations are not enumerated to delegates.
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import type { AdminUserRow, AdminUsersResponse } from '../../shared/api-types.js';
import { SUBJECT_PROVIDER_LABELS } from '../../shared/enums.js';
import { failureKind, queryString, type ApiFailure, type ApiResult } from '../api.js';
import { PATHS } from '../paths.js';
import { AdminSectionStrip } from '../shell/admin.js';
import { useMeContext } from '../shell/me-context.js';
import {
  Badge,
  Button,
  Chip,
  ConfirmDialog,
  DataTable,
  Dialog,
  Icon,
  IconButton,
  LinkButton,
  Menu,
  Page,
  PageHead,
  RelativeTime,
  Section,
  SelectField,
  Skeleton,
  TextField,
  formatBadgeCount,
  formatCount,
  inlineFailureText,
  pluralise,
  useIsMobile,
  useRequest,
  useToast,
  type DataTableColumn,
  type DataTableStatus,
  type MenuEntry,
} from '../ui/index.js';
import { adminApi } from './admin-api.js';
import {
  EMPTY_ROSTER_FILTERS,
  activeRosterFilterCount,
  describeRosterFilters,
  filterRoster,
  parseRosterFilters,
  pendingCountKnown,
  rosterFacets,
  rosterFiltersActive,
  rosterHeadingText,
  rosterSortValue,
  summariseBulk,
  writeRosterFilters,
  type BulkStep,
  type RosterFilters,
  type RosterSortKey,
} from './admin-helpers.js';
import { AccountDeletionBody, ApproveDialog, IdentityCell, OperationFailure, RightsChips, StatePill } from './parts.js';
import './admin.css';

type Confirmation =
  | { kind: 'refuse' | 'deny' | 'suspend' | 'revoke' | 'delete'; row: AdminUserRow };

interface ApprovalRequest {
  accounts: AdminUserRow[];
  mode: 'approve' | 'reinstate';
}

/** The roster came back empty although the viewer is signed in: a fetch or permission problem, not an empty state. */
const EMPTY_ROSTER_FAILURE: ApiFailure = {
  ok: false,
  status: 200,
  code: 'unexpected_response',
  message: 'The list of accounts came back empty, which cannot be right while you are signed in. Try again.',
  correlationId: null,
};

export default function AdminUsersPage() {
  const { me, reload: reloadMe } = useMeContext();
  const isSuperuser = me.subject.isSuperuser;
  const canSeeAudit = me.admin.audit.view;
  const toast = useToast();
  const users = useRequest<AdminUsersResponse>((signal) => adminApi.users(signal), []);
  const [params, setParams] = useSearchParams();
  const filters = useMemo(() => parseRosterFilters(params), [params]);
  const setFilters = useCallback((next: RosterFilters) => {
    setParams((current) => writeRosterFilters(current, next), { replace: true });
  }, [setParams]);

  const pending = users.data?.pending ?? [];
  const roster = users.data?.roster ?? [];
  const total = users.data?.total ?? 0;
  const facets = useMemo(() => rosterFacets(roster, filters), [roster, filters]);
  const matching = useMemo(() => filterRoster(roster, filters), [roster, filters]);
  const filtering = rosterFiltersActive(filters);

  // --- Mutations --------------------------------------------------------------------------------
  const navigate = useNavigate();
  const [approval, setApproval] = useState<ApprovalRequest | null>(null);
  const [approvalError, setApprovalError] = useState<ApiFailure | null>(null);
  const [confirmation, setConfirmation] = useState<Confirmation | null>(null);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<ApiFailure | null>(null);
  const [attempted, setAttempted] = useState('change this account');
  const [progress, setProgress] = useState<string | null>(null);
  const [bulkFailures, setBulkFailures] = useState<{ name: string; message: string }[]>([]);

  const auditLinkFor = (row: Pick<AdminUserRow, 'id'>): string => `${PATHS.adminAudit}${queryString({ target: row.id, targetType: 'subject' })}`;

  const reportFailure = useCallback((result: ApiFailure, what: string): void => {
    setAttempted(what);
    setFailure(result);
    // Why: a 403 means the server re-resolved our permission and refused; the shell and the page
    // must show what we actually hold now, not what we held when the page loaded.
    if (failureKind(result) === 'denied') {
      reloadMe();
      users.reload();
    }
  }, [reloadMe, users]);

  /** Offers the audit trail from a success toast, arriving on the newest record about the account. */
  const auditToastAction = (row: AdminUserRow) => (canSeeAudit
    ? { action: { label: 'View audit trail', onClick: () => navigate(`${auditLinkFor(row)}&highlight=latest`) } }
    : undefined);

  const runSingle = async (what: string, call: () => Promise<ApiResult<unknown>>, success: string, row: AdminUserRow): Promise<void> => {
    setBusy(true);
    setFailure(null);
    const result = await call();
    setBusy(false);
    // Why: the confirmation closes either way; a failure is stated at the top of the page, where
    // it stays readable after the dialog has gone, and a 403 re-syncs what we hold.
    setConfirmation(null);
    if (!result.ok) {
      reportFailure(result, what);
      return;
    }
    users.reload();
    toast.success(success, auditToastAction(row));
  };

  const confirmAction = async (): Promise<void> => {
    if (!confirmation) return;
    const { kind, row } = confirmation;
    const name = row.displayName;
    switch (kind) {
      case 'refuse':
        await runSingle(`refuse ${name}'s request`, () => adminApi.deny(row.id), `Refused ${name}'s request.`, row);
        break;
      case 'deny':
        await runSingle(`deny ${name}`, () => adminApi.deny(row.id), `Denied ${name}. They are signed out everywhere and hold no groups.`, row);
        break;
      case 'suspend':
        await runSingle(`suspend ${name}`, () => adminApi.suspend(row.id), `Suspended ${name}. They are signed out everywhere and hold no groups.`, row);
        break;
      case 'revoke':
        await runSingle(`sign ${name} out everywhere`, () => adminApi.revokeSessions(row.id), `Signed ${name} out everywhere.`, row);
        break;
      case 'delete':
        await runSingle(`delete ${name}'s account`, () => adminApi.deleteUser(row.id), `Deleted ${name}'s account. Audit records remain.`, row);
        break;
      default:
        break;
    }
  };

  const closeApproval = (): void => {
    setApproval(null);
    setApprovalError(null);
    setBulkFailures([]);
    setProgress(null);
  };

  const approve = async (groupIds: string[]): Promise<void> => {
    if (!approval) return;
    const words = approval.mode === 'reinstate' ? { doing: 'Reinstating', done: 'Reinstated' } : { doing: 'Approving', done: 'Approved' };
    const what = approval.mode === 'reinstate' ? 'reinstate this account' : 'approve this account';
    setBusy(true);
    setApprovalError(null);
    setBulkFailures([]);
    const steps: BulkStep[] = [];
    const count = approval.accounts.length;
    let denied: ApiFailure | null = null;
    let lastFailure: ApiFailure | null = null;
    for (const [index, account] of approval.accounts.entries()) {
      if (count > 1) setProgress(`${words.doing} ${index + 1} of ${count} — ${account.displayName}…`);
      const result = await adminApi.approve(account.id, groupIds);
      // Why `inlineFailureText` (ruling Q11): each account's line in the dialog names a refusal's next step, as the pop-ups do.
      steps.push({ id: account.id, name: account.displayName, ok: result.ok, message: result.ok ? undefined : inlineFailureText(result) });
      if (result.ok) continue;
      lastFailure = result;
      if (failureKind(result) === 'denied') {
        denied = result;
        break; // Why: the rest would fail identically; stop rather than hammer a refused route.
      }
    }
    setProgress(null);
    setBusy(false);
    users.reload();
    if (denied) {
      closeApproval();
      reportFailure(denied, what);
      return;
    }
    if (count === 1) {
      if (lastFailure) {
        setApprovalError(lastFailure);
        return;
      }
      toast.success(`${words.done} ${approval.accounts[0]?.displayName ?? 'the account'}.`, approval.accounts[0] ? auditToastAction(approval.accounts[0]) : undefined);
      closeApproval();
      return;
    }
    const summary = summariseBulk(steps);
    const failed = steps.filter((step) => !step.ok);
    if (failed.length === 0) {
      toast.success(summary.message);
      closeApproval();
      return;
    }
    setBulkFailures(failed.map((step) => ({ name: step.name, message: step.message ?? 'Not accepted.' })));
    // Why: a retry from the open dialog must post only the accounts that were not approved; the
    // ones that went through would otherwise answer 409 and be reported as failures again.
    const failedIds = new Set(failed.map((step) => step.id));
    setApproval({ ...approval, accounts: approval.accounts.filter((account) => failedIds.has(account.id)) });
    toast[summary.tone === 'error' ? 'error' : 'warning'](summary.message);
  };

  // --- Tables -----------------------------------------------------------------------------------
  const selfId = me.subject.id;

  const pendingColumns = useMemo<DataTableColumn<AdminUserRow>[]>(() => {
    const columns: DataTableColumn<AdminUserRow>[] = [
      { key: 'name', header: 'Identity', card: 'title', width: '16rem', sortValue: (row) => `${row.displayName} ${row.email}`, cell: (row) => <IdentityCell row={row} to={PATHS.adminUser(row.id)} /> },
      { key: 'requested_at', header: 'Requested', type: 'date', sortValue: (row) => row.requestedAt, cell: (row) => <RelativeTime value={row.requestedAt} /> },
      { key: 'provider', header: 'Signs in with', sortValue: (row) => SUBJECT_PROVIDER_LABELS[row.provider], cell: (row) => SUBJECT_PROVIDER_LABELS[row.provider] },
    ];
    if (isSuperuser) {
      columns.push({
        key: 'actions',
        header: <span className="visually-hidden">Actions</span>,
        name: 'Actions',
        sortable: false,
        align: 'end',
        card: 'actions',
        className: 'admin-actions-cell',
        cell: (row) => (
          <span className="row-actions">
            <Button size="sm" variant="primary" onClick={() => setApproval({ accounts: [row], mode: 'approve' })}>Approve</Button>
            <Button size="sm" variant="danger-outline" onClick={() => setConfirmation({ kind: 'refuse', row })}>Refuse</Button>
          </span>
        ),
      });
    }
    return columns;
  }, [isSuperuser]);

  // Folding (04-data §1.7; ADR 0168; 06-modules §1.4): Decided and Last active first, then State — reachable from the
  // row's disclosure. Identity and rights attribution — the module's central column — never fold (§1.5 rule 5), nor do
  // the actions. Measured, the roster fits from --bp-md with two pixels to spare on a coarse pointer; declared so that a
  // wider row folds rather than scrolls.
  const rosterColumns = useMemo<DataTableColumn<AdminUserRow>[]>(() => {
    const sortBy = (key: RosterSortKey) => (row: AdminUserRow) => rosterSortValue(row, key);
    const columns: DataTableColumn<AdminUserRow>[] = [
      {
        key: 'name',
        header: 'Identity',
        card: 'title',
        width: '16rem',
        sortValue: sortBy('name'),
        cell: (row) => <IdentityCell row={row} to={PATHS.adminUser(row.id)} aside={row.id === selfId ? 'You' : undefined} />,
      },
      {
        key: 'rights',
        header: 'Rights',
        headerTitle: 'Where each right comes from',
        sortValue: sortBy('rights'),
        cell: (row) => <RightsChips rights={row.rights} linkGroups={me.admin.groups} who={row.displayName} />,
      },
      { key: 'state', header: 'State', type: 'enum', fold: 3, sortValue: sortBy('state'), cell: (row) => <StatePill status={row.status} /> },
      { key: 'last_seen', header: 'Last active', type: 'date', fold: 2, sortValue: sortBy('last_seen'), cell: (row) => <RelativeTime value={row.lastSeenAt} /> },
      {
        key: 'decided',
        header: 'Decided',
        type: 'date',
        fold: 1,
        sortValue: sortBy('decided'),
        cell: (row) => (
          <span className="stack stack--1">
            <RelativeTime value={row.decidedAt} />
            {row.decidedBy ? <span className="text-xs muted">by {row.decidedBy.name}</span> : null}
          </span>
        ),
      },
      {
        key: 'actions',
        header: <span className="visually-hidden">Actions</span>,
        name: 'Actions',
        sortable: false,
        align: 'end',
        card: 'actions',
        className: 'admin-actions-cell',
        cell: (row) => (
          <RowActions
            row={row}
            isSelf={row.id === selfId}
            isSuperuser={isSuperuser}
            auditLink={canSeeAudit ? auditLinkFor(row) : null}
            onApprove={(mode) => setApproval({ accounts: [row], mode })}
            onConfirm={(kind) => setConfirmation({ kind, row })}
          />
        ),
      },
    ];
    return columns;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuperuser, canSeeAudit, selfId, me.admin.groups]);

  const status: DataTableStatus = users.status === 'loading'
    ? { kind: 'loading', rowCount: 6 }
    : users.status === 'error' && users.error
      ? { kind: 'error', failure: users.error, onRetry: users.reload }
      : { kind: 'ready' };

  // Why: an empty roster while the viewer is themselves an account is a failure wearing an empty
  // state (06-modules §1.8); it is presented as one, with a retry, never as "No users".
  const rosterStatus: DataTableStatus = status.kind === 'ready' && total === 0
    ? { kind: 'error', failure: EMPTY_ROSTER_FAILURE, onRetry: users.reload }
    : status;

  // Why no count unless it is known (06-modules §1.3 R2, §1.8; Phase 1c D22): a failed or refused load once read "0
  // pending" and "Accounts (0)" above the failure — a zero stated as fact. The failure owns the space now.
  const pendingHeading = (
    <span className="count-heading">
      Pending requests
      {users.status === 'loading'
        ? <Skeleton width="2rem" className="count-heading__skeleton" />
        : pendingCountKnown(users.status) ? <Badge tone={pending.length > 0 ? 'accent' : 'neutral'} label={`${pending.length} pending`}>{formatBadgeCount(pending.length)}</Badge> : null}
    </span>
  );

  const rosterHeading = users.status === 'loading'
    ? <span className="count-heading">Accounts <Skeleton width="2rem" className="count-heading__skeleton" /></span>
    : rosterHeadingText(users.status, total, matching.length, filtering);

  const clearFilters = () => setFilters({ ...EMPTY_ROSTER_FILTERS });

  return (
    <Page measure="wide">
      <PageHead title="Users" strip={<AdminSectionStrip />} />

      <OperationFailure failure={failure} attempted={attempted} onDismiss={() => setFailure(null)} />

      <Section
        id="pending"
        title={pendingHeading}
        description={isSuperuser ? undefined : 'A superuser decides each request.'}
        actions={isSuperuser && pending.length > 1 ? (
          <Button variant="secondary" icon="check" onClick={() => setApproval({ accounts: pending, mode: 'approve' })}>
            {pluralise(pending.length, { one: 'Approve # request', other: 'Approve all # requests' })}
          </Button>
        ) : undefined}
      >
        <DataTable<AdminUserRow, string>
          caption="Pending requests"
          failureNoun="pending requests"
          columns={pendingColumns}
          rows={pending}
          rowKey={(row) => row.id}
          status={status.kind === 'loading' ? { kind: 'loading', rowCount: 2 } : status}
          sort={{ defaultSort: { key: 'requested_at', dir: 'asc' }, namespace: 'pending' }}
          clientSort
          empty={{ title: 'Nothing awaiting review', description: 'New sign-ups appear here until an administrator approves or refuses them.' }}
        />
      </Section>

      <Section id="roster" title={rosterHeading}>
        <RosterFilterBar filters={filters} facets={facets} onChange={setFilters} onClear={clearFilters} matching={matching.length} total={total} loading={users.status === 'loading'} countKnown={users.status === 'ready' && total > 0} />
        <DataTable<AdminUserRow, string>
          caption="Accounts"
          failureNoun="accounts"
          columns={rosterColumns}
          rows={matching}
          rowKey={(row) => row.id}
          status={rosterStatus}
          sort={{ defaultSort: { key: 'name', dir: 'asc' } }}
          clientSort
          empty={{ title: 'No accounts yet', description: 'Approved accounts appear here with their rights.' }}
          filtered={{
            active: filtering,
            onClear: clearFilters,
            title: 'No accounts match these filters',
            description: describeRosterFilters(filters, facets).map((chip) => chip.label).join(' · '),
          }}
        />
      </Section>

      {approval ? (
        <ApproveDialog
          open
          accounts={approval.accounts}
          mode={approval.mode}
          onCancel={closeApproval}
          onConfirm={approve}
          busy={busy}
          progress={progress}
          failures={bulkFailures}
          error={approvalError}
        />
      ) : null}

      <ConfirmationDialogs confirmation={confirmation} busy={busy} onCancel={() => setConfirmation(null)} onConfirm={confirmAction} />
    </Page>
  );
}

// --- Row actions ---------------------------------------------------------------------------------

interface RowActionsProps {
  row: AdminUserRow;
  isSelf: boolean;
  isSuperuser: boolean;
  auditLink: string | null;
  onApprove: (mode: 'approve' | 'reinstate') => void;
  onConfirm: (kind: Confirmation['kind']) => void;
}

/** Why the lifecycle actions are absent on a superuser's row: the deployment, not this screen, manages it. */
const SUPERUSER_ROW_NOTE = 'Superuser — managed by the deployment';

/**
 * At most two inline, the rest in an always-visible overflow, destructive last (04-data §1.2.2).
 * The signed-in administrator's own row offers no lock-out action and says so in place
 * (06-modules §1.6). Delegates see only what they could ever do: open the account, reach its audit.
 * Another superuser's row offers only sign-out-everywhere: deny, suspend and delete would be undone
 * at their next sign-in (the server refuses them, 409 superuser_env_managed), so they are not shown
 * and the row says why (05-client §5.5).
 */
function RowActions({ row, isSelf, isSuperuser, auditLink, onApprove, onConfirm }: RowActionsProps) {
  const name = row.displayName;
  const entries: MenuEntry[] = [];
  if (auditLink) entries.push({ id: 'audit', label: 'View audit trail', icon: 'list', to: auditLink });

  let second: ReactNode = null;
  if (isSuperuser && !isSelf && row.isSuperuser) {
    if (row.status === 'approved') {
      second = <Button size="sm" variant="secondary" onClick={() => onConfirm('revoke')}>Sign out everywhere</Button>;
    }
  } else if (isSuperuser && !isSelf) {
    if (row.status === 'approved') {
      second = <Button size="sm" variant="danger-outline" onClick={() => onConfirm('suspend')}>Suspend</Button>;
      entries.push({ id: 'revoke', label: 'Sign out everywhere', icon: 'sign-out', onSelect: () => onConfirm('revoke') });
      entries.push({ id: 'deny', label: 'Deny access', icon: 'x', onSelect: () => onConfirm('deny') });
    } else if (row.status === 'suspended' || row.status === 'denied') {
      second = <Button size="sm" variant="secondary" onClick={() => onApprove('reinstate')}>Reinstate</Button>;
      if (row.status === 'suspended') entries.push({ id: 'deny', label: 'Deny access', icon: 'x', onSelect: () => onConfirm('deny') });
    } else if (row.status === 'pending') {
      second = <Button size="sm" variant="primary" onClick={() => onApprove('approve')}>Approve</Button>;
    }
    entries.push({ id: 'delete', label: 'Delete account', icon: 'trash', danger: true, onSelect: () => onConfirm('delete') });
  }

  return (
    <span className="row-actions">
      {isSelf && isSuperuser ? <span className="row-actions__note">No lock-out actions on your own account</span> : null}
      {!isSelf && isSuperuser && row.isSuperuser ? <span className="row-actions__note">{SUPERUSER_ROW_NOTE}</span> : null}
      <LinkButton size="sm" variant="secondary" to={PATHS.adminUser(row.id)}>Open</LinkButton>
      {second}
      {entries.length > 0 ? (
        <Menu
          label={`More actions for ${name}`}
          entries={entries}
          trigger={(props) => <IconButton {...props} icon="more-horizontal" label={`More actions for ${name}`} size="sm" />}
        />
      ) : null}
    </span>
  );
}

// --- Confirmations -------------------------------------------------------------------------------

function ConfirmationDialogs({ confirmation, busy, onCancel, onConfirm }: { confirmation: Confirmation | null; busy: boolean; onCancel: () => void; onConfirm: () => Promise<void> }) {
  const row = confirmation?.row;
  const name = row?.displayName ?? 'this account';
  const kind = confirmation?.kind;
  const common = { open: confirmation !== null, busy, onCancel, onConfirm };
  switch (kind) {
    case 'refuse':
      return (
        <ConfirmDialog {...common} title={`Refuse ${name}'s request?`} confirmLabel="Refuse" danger>
          <p>They will not be able to sign in. Their request stays on file, and you can approve them later.</p>
        </ConfirmDialog>
      );
    case 'deny':
      return (
        <ConfirmDialog {...common} title={`Deny ${name} access?`} confirmLabel="Deny access" danger>
          <p>They are signed out everywhere, removed from every group, and cannot sign in until reinstated. Their meetings and to-dos are kept.</p>
        </ConfirmDialog>
      );
    case 'suspend':
      return (
        <ConfirmDialog {...common} title={`Suspend ${name}?`} confirmLabel="Suspend" danger>
          <p>They are signed out everywhere and removed from every group. Reinstating later means choosing their groups again. Their meetings and to-dos are kept.</p>
        </ConfirmDialog>
      );
    case 'revoke':
      return (
        <ConfirmDialog {...common} title={`Sign ${name} out everywhere?`} confirmLabel="Sign out everywhere">
          <p>Every active session ends now. Their groups are unchanged and they can sign in again straight away.</p>
        </ConfirmDialog>
      );
    case 'delete':
      return (
        <ConfirmDialog {...common} title={`Delete ${name}'s account?`} confirmLabel="Delete account" danger typeToConfirm={row?.email}>
          <AccountDeletionBody />
        </ConfirmDialog>
      );
    default:
      return null;
  }
}

// --- Filter bar (R3) -----------------------------------------------------------------------------

export interface RosterFilterBarProps {
  filters: RosterFilters;
  facets: ReturnType<typeof rosterFacets>;
  onChange: (next: RosterFilters) => void;
  onClear: () => void;
  matching: number;
  total: number;
  loading: boolean;
  /** False when the load failed, was refused or read as a failure: the count region then announces no number (D22). */
  countKnown?: boolean;
}

/**
 * Free-text search across name and email (its reach stated in the label), one control per facet
 * the permission model defines — group, administrative section (06-modules §1.3 R3, A15; D8),
 * capability, state — offering only values in the loaded set, active filters as removable chips,
 * and one Clear all. Below --bp-md the facets collapse
 * into a sheet with an explicit Apply while the search and the chip rail stay on the page. Exported for its markup test.
 */
export function RosterFilterBar({ filters, facets, onChange, onClear, matching, total, loading, countKnown = true }: RosterFilterBarProps) {
  const isMobile = useIsMobile();
  const [draftSearch, setDraftSearch] = useState(filters.q);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [draft, setDraft] = useState<RosterFilters>(filters);
  const debounce = useRef<number | null>(null);
  const filtersRef = useRef(filters);
  filtersRef.current = filters;

  useEffect(() => { setDraftSearch(filters.q); }, [filters.q]);
  useEffect(() => () => { if (debounce.current !== null) window.clearTimeout(debounce.current); }, []);

  // Why: ~300 ms debounce (03-components §10) so the URL is not rewritten on every keystroke.
  const onSearch = (value: string): void => {
    setDraftSearch(value);
    if (debounce.current !== null) window.clearTimeout(debounce.current);
    debounce.current = window.setTimeout(() => onChange({ ...filtersRef.current, q: value }), 300);
  };

  const activeCount = activeRosterFilterCount(filters);
  const chips = describeRosterFilters(filters, facets);
  const remove = (key: string): void => {
    if (key === 'q' || key === 'group' || key === 'section' || key === 'capability' || key === 'state') onChange({ ...filters, [key]: '' });
  };
  const facetFields = (value: RosterFilters, set: (next: RosterFilters) => void) => (
    <>
      <SelectField className="filter-bar__facet" label="Group" value={value.group} onChange={(event) => set({ ...value, group: event.target.value })} options={facets.groups} placeholderOption="Any group" disabled={loading} />
      <SelectField className="filter-bar__facet" label="Section" value={value.section} onChange={(event) => set({ ...value, section: event.target.value })} options={facets.sections} placeholderOption="Any section" disabled={loading} />
      <SelectField className="filter-bar__facet" label="Capability" value={value.capability} onChange={(event) => set({ ...value, capability: event.target.value })} options={facets.capabilities} placeholderOption="Any capability" disabled={loading} />
      <SelectField className="filter-bar__facet" label="State" value={value.state} onChange={(event) => set({ ...value, state: event.target.value })} options={facets.states} placeholderOption="Any state" disabled={loading} />
    </>
  );

  // Why a clear of its own: it cancels a keystroke still waiting in the debounce, or the old text would land in the URL
  // after the field was emptied.
  const onClearSearch = (): void => {
    if (debounce.current !== null) window.clearTimeout(debounce.current);
    debounce.current = null;
    setDraftSearch('');
    onChange({ ...filtersRef.current, q: '' });
  };

  return (
    <div className="filter-bar">
      <div className="filter-bar__controls">
        {/* Why this anatomy: 03-components §10 — a leading search icon and a clear affordance once non-empty — which
            06-modules §1.3 R3 binds this bar to; the browser's own cancel control (absent in Firefox) was the only one
            here, while the To-dos bar carried both. Same classes as the To-dos bar, one declaration in components.css
            (conformity 2026-10-05, R1; owner's pick C). */}
        <div className="filter-bar__search">
          <Icon name="search" size={16} className="filter-bar__search-icon" />
          <TextField
            className="filter-bar__search-field"
            label="Search name and email"
            type="search"
            inputMode="search"
            autoComplete="off"
            value={draftSearch}
            onChange={(event) => onSearch(event.target.value)}
            placeholder="ada@centro.team"
          />
          {draftSearch !== '' ? (
            <IconButton icon="x" label="Clear search" size="sm" className="filter-bar__search-clear" onClick={onClearSearch} />
          ) : null}
        </div>
        {isMobile ? (
          <Button
            className="filter-bar__trigger"
            variant="secondary"
            icon="filter"
            onClick={() => { setDraft(filters); setSheetOpen(true); }}
            aria-label={activeCount > 0 ? `Filters, ${activeCount} active` : 'Filters'}
          >
            Filters{activeCount > 0 ? <> <Badge tone="accent">{formatBadgeCount(activeCount)}</Badge></> : null}
          </Button>
        ) : facetFields(filters, onChange)}
      </div>

      <div className="filter-bar__active">
        {/* Why hidden: the section heading already carries the total and, filtered, both counts
            (06-modules §1.3 R2); the live region still announces them (03-components §10). */}
        <p className="filter-bar__count visually-hidden" role="status" aria-live="polite">
          {loading ? 'Loading accounts…' : !countKnown ? '' : rosterFiltersActive(filters) ? `Showing ${formatCount(matching)} of ${pluralise(total, { one: '# account', other: '# accounts' })}` : pluralise(total, { one: '# account', other: '# accounts' })}
        </p>
        {chips.length > 0 ? (
          // Why a named list: the to-dos filter bar groups its chips the same way, so assistive
          // technology announces "Active filters" identically on every filtered screen (03-components §10).
          <ul className="cluster" role="list" aria-label="Active filters">
            {chips.map((chip) => (
              <li key={chip.key}>
                <Chip selected onRemove={() => remove(chip.key)} removeLabel={`Remove filter: ${chip.label}`}>{chip.label}</Chip>
              </li>
            ))}
            <li>
              <Button variant="link" size="sm" onClick={onClear}>Clear all</Button>
            </li>
          </ul>
        ) : null}
      </div>

      {isMobile ? (
        <Dialog
          open={sheetOpen}
          onClose={() => setSheetOpen(false)}
          title="Filters"
          size="sm"
          initialFocus="first"
          actions={
            <>
              <Button variant="secondary" onClick={() => setSheetOpen(false)}>Cancel</Button>
              <Button variant="primary" onClick={() => { onChange({ ...draft, q: filters.q }); setSheetOpen(false); }}>Apply</Button>
            </>
          }
        >
          <div className="stack stack--3">{facetFields(draft, setDraft)}</div>
        </Dialog>
      ) : null}
    </div>
  );
}
